import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { applyPromoCode, clearPromoConfirm } from '@/store/actions';

/** Human-readable label for the current subscription discount (from users/refresh). */
const formatDiscount = (d: any): string => {
  if (!d) return '';
  let amount = 'Discount';
  if (d.percentOff != null) {
    amount = `${d.percentOff}% off`;
  } else if (d.amountOff != null) {
    const currency = (d.currency || 'usd').toUpperCase();
    amount = currency === 'USD'
      ? `$${(d.amountOff / 100).toFixed(2)} off`
      : `${(d.amountOff / 100).toFixed(2)} ${currency} off`;
  }
  let duration = '';
  if (d.duration === 'forever') duration = ' · ongoing';
  else if (d.duration === 'once') duration = ' · one time';
  else if (d.duration === 'repeating' && d.durationInMonths) duration = ` · ${d.durationInMonths} months`;
  const detail = `${amount}${duration}`;
  return d.code ? `${d.code} (${detail})` : detail;
};

/**
 * Lets an existing subscriber apply a promo code to their live subscription. The discount lands on
 * the next charge (Stripe applies it per the coupon's own duration). Success/error feedback comes
 * back as a toast from the saga; the only local UI here is the input and the replace-confirm dialog.
 */
const PromoCodeField = ({ user }: any) => {
  const dispatch = useDispatch();
  const promoConfirmCode = useSelector((state: any) => state?.subscription?.promoConfirmCode);
  const appliedDiscount = useSelector((state: any) => state?.subscription?.appliedDiscount);

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');

  // Collapse and clear once a code has been successfully applied.
  useEffect(() => {
    if (appliedDiscount) {
      setCode('');
      setOpen(false);
    }
  }, [appliedDiscount]);

  const handleApply = () => {
    const trimmed = code.trim();
    if (!trimmed) return;
    dispatch(applyPromoCode({ userId: user?.id, code: trimmed }));
  };

  const handleConfirmReplace = () => {
    dispatch(applyPromoCode({ userId: user?.id, code: promoConfirmCode, confirmReplace: true }));
  };

  const handleCancelReplace = () => {
    dispatch(clearPromoConfirm());
  };

  const discountLabel = formatDiscount(user?.subscriptionDiscount);

  return (
    <Box mt={1.5}>
      {discountLabel && (
        <Box
          sx={{
            backgroundColor: '#FFF3CD',
            border: '1px solid #FFE69C',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: 1,
          }}>
          <Typography fontSize={'0.85rem'} color='#856404'>
            Promo applied: <strong>{discountLabel}</strong>
          </Typography>
        </Box>
      )}
      {!open ? (
        <Typography
          variant='button'
          onClick={() => setOpen(true)}
          sx={{ cursor: 'pointer', textDecoration: 'underline', color: '#0072CE', fontSize: '0.8rem' }}>
          {discountLabel ? 'Replace promo code?' : 'Have a promo code?'}
        </Typography>
      ) : (
        <Box>
          <Typography fontSize={'0.9rem'} color={'#1D1D1D'} mb={0.5}>
            Promo code
          </Typography>
          <Box display='flex' gap={1} alignItems='center'>
            <TextField
              size='small'
              placeholder='Enter code'
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleApply();
              }}
              inputProps={{ 'aria-label': 'Promo code' }}
              sx={{ backgroundColor: '#fff', borderRadius: 1, flex: 1, maxWidth: '16rem' }}
            />
            <Button
              variant='contained'
              onClick={handleApply}
              disabled={!code.trim()}
              sx={{ textTransform: 'none' }}>
              Apply
            </Button>
          </Box>
          <Typography fontSize={'0.75rem'} color='#5C6372' mt={0.5}>
            Applied to your next charge.
          </Typography>
        </Box>
      )}

      <Dialog
        open={!!promoConfirmCode}
        onClose={handleCancelReplace}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}>
        <DialogTitle sx={{ color: '#1D1D1D', fontSize: '1.1rem' }}>Replace current discount?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#5C6372' }}>
            You already have a discount on your subscription. Applying this code will replace it.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={handleCancelReplace} sx={{ textTransform: 'none', color: '#5C6372' }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmReplace} variant='contained' sx={{ textTransform: 'none' }}>
            Replace
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromoCodeField;
